import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { UsersTypes } from '@frontend/user-helpers';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import { RECOMMENDED_ROLES } from '../../constants';
import { useUserRoles } from '../../hooks';

type UserRoleCardsProps = {
  onChange: (roles: string[]) => void;
};

const RoleButton = ({
  onClick,
  iconName,
  title,
  description,
}: {
  onClick: () => void;
  iconName: IconName;
  title: string;
  description: string;
}) => (
  <NakedButton css={styles.roleButton} onClick={onClick}>
    <Icon name={iconName} />
    <Text weight='bold'>{title}</Text>
    <span>{description}</span>
  </NakedButton>
);

const useRoleMappings = () => {
  const { allAccessRoleNameList, isLoading } = useUserRoles();

  if (isLoading || !allAccessRoleNameList) {
    return { roleMap: null };
  }

  return {
    roleMap: {
      [UsersTypes.USER_TYPE.BASIC_PERMISSIONS]: RECOMMENDED_ROLES,
      [UsersTypes.USER_TYPE.ADMIN_ACCESS]: allAccessRoleNameList,
      [UsersTypes.USER_TYPE.PHONE_ADMIN]: ['Phone Admin'],
    },
  };
};

export const UserRoleQuickLinks = ({ onChange }: UserRoleCardsProps) => {
  const { t } = useTranslation('account');
  const { roleMap } = useRoleMappings();

  if (!roleMap) {
    return null;
  }

  const handleRoleClick = (role: UsersTypes.USER_TYPE) => {
    onChange(roleMap[role]);
  };

  return (
    <section css={styles.sectionStyle}>
      <RoleButton
        onClick={() => handleRoleClick(UsersTypes.USER_TYPE.BASIC_PERMISSIONS)}
        iconName='lock'
        title={t('Basic Access')}
        description={t('Grant users access to essential Weave features and products for day-to-day use.')}
      />
      <RoleButton
        onClick={() => handleRoleClick(UsersTypes.USER_TYPE.ADMIN_ACCESS)}
        iconName='family'
        title={t('Admin Access')}
        description={t(
          'Grant users full control, including the ability to read, write, and manage transactions across Weave tools.'
        )}
      />
    </section>
  );
};

const styles = {
  roleButton: css`
    display: flex;
    flex-direction: column;
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing(2)};
    align-items: center;
    gap: ${theme.spacing(1)};
    width: 370px;

    & > span {
      color: ${theme.colors.neutral50};
      font-size: ${theme.font.size.medium};
    }

    & > p {
      color: ${theme.colors.neutral70};
      font-size: ${theme.font.size.medium};
    }

    &:hover {
      background-color: ${theme.colors.neutral5};
    }

    &:active {
      background-color: ${theme.colors.neutral10};
    }
  `,

  sectionStyle: css`
    display: flex;
    gap: ${theme.spacing(4)};
    min-width: 0;
  `,
};
